import { Injectable } from '@angular/core';
import {
  CreateDaydreamFacadeRequest, DaydreamDetails, GetDaydreamDetailsRequest, GetSituFacadeResponse, ListDaydreamDetailsRequest,
  ListDaydreamHomesRequest, RenderedOutputHome, RenderedOutputStatus, SituViewWebApiClient,
} from 'situview-webapi-client';
import { ConfigService } from 'src/app/services/config.service';
import { SettingsDocument } from '../engine';

/** The image SituView writes into a Daydream artifact. */
export const DAYDREAM_IMAGE_FILE = 'facade.jpg';
/** The situFACADE image a Daydream is made from unless another of its images is picked. */
export const DEFAULT_SOURCE_FILE = 'facade.jpg';
/** SituView serves at most this many Daydreams a page. */
export const DAYDREAM_PAGE_SIZE = 20;

export interface DaydreamGenerateArgs {
  companyId: string;
  /** The parent situFACADE rendered output id, i.e. the situlink id. */
  renderedOutputId: string;
  /** Which of the parent's images to daydream from. */
  sourceFileName: string;
  promptText: string;
  promptJson: string;
}

export interface DaydreamPage {
  items: DaydreamDetails[];
  totalItems: number;
}

export const isPending = (status?: RenderedOutputStatus) =>
  status === RenderedOutputStatus.Queued || status === RenderedOutputStatus.InProgress;

@Injectable({ providedIn: 'root' })
export class DaydreamService {

  /** Full details already read, only of Daydreams that can no longer change. List rows never go in here. */
  private details = new Map<string, DaydreamDetails>();
  /** Reads in flight, so a second ask for the same Daydream (hover then click) joins the first. */
  private reading = new Map<string, Promise<DaydreamDetails>>();

  constructor(
    private situViewClient: SituViewWebApiClient,
    private configService: ConfigService) { }

  /**
   * A Daydream or a situFACADE of the company, from memory if it has been read before. Throws if it
   * can't be read, including when it belongs to another company.
   */
  async getDetails(companyId: string, renderedOutputId: string): Promise<DaydreamDetails> {
    const known = this.details.get(renderedOutputId);
    if (known?.companyId === companyId) { return known; }
    const key = `${companyId}/${renderedOutputId}`;
    let reading = this.reading.get(key);
    if (!reading) {
      reading = this.readDetails(companyId, renderedOutputId).finally(() => this.reading.delete(key));
      this.reading.set(key, reading);
    }
    return reading;
  }

  /** Always from SituView: for following a Daydream that is still being generated. */
  async readDetails(companyId: string, renderedOutputId: string): Promise<DaydreamDetails> {
    const request = new GetDaydreamDetailsRequest();
    request.companyId = companyId;
    request.renderedOutputId = renderedOutputId;
    const response = await this.situViewClient.getDaydreamDetails(request);
    if (!response?.success || !response.details) {
      throw new Error(response?.errorMessages?.join(' ') || 'This situFACADE could not be loaded.');
    }
    this.remember(response.details);
    return response.details;
  }

  /**
   * A page of a company's complete Daydreams, newest first. Summary rows only: SituView leaves
   * imageFiles and settingsJson null on them so the list is one database query. Use getDetails for
   * a row's settings. A homeId narrows the page to one home.
   */
  async listDetails(companyId: string, pageNumber: number, mineOnly: boolean, homeId?: string): Promise<DaydreamPage> {
    const request = new ListDaydreamDetailsRequest();
    request.companyId = companyId;
    request.pageNumber = pageNumber;
    request.pageSize = DAYDREAM_PAGE_SIZE;
    request.mineOnly = mineOnly;
    request.homeId = homeId;
    const response = await this.situViewClient.listDaydreamDetails(request);
    if (!response?.success) {
      throw new Error(response?.errorMessages?.join(' ') || 'Previous Daydreams could not be loaded.');
    }
    const items = response.items ?? [];
    return { items, totalItems: response.totalItems ?? items.length };
  }

  /** The homes that have Daydreams, with counts, for the history's home filter. Respects mineOnly. */
  async listHomes(companyId: string, mineOnly: boolean): Promise<RenderedOutputHome[]> {
    const request = new ListDaydreamHomesRequest();
    request.companyId = companyId;
    request.mineOnly = mineOnly;
    const response = await this.situViewClient.listDaydreamHomes(request);
    if (!response?.success) {
      throw new Error(response?.errorMessages?.join(' ') || 'The homes with Daydreams could not be loaded.');
    }
    return response.homes ?? [];
  }

  /** The settings document a Daydream was generated with, or null. Always null for a list row. */
  settingsOf(details: DaydreamDetails | undefined): SettingsDocument | null {
    const json = details?.settingsJson;
    if (!json || !json.trim() || json.trim() === '{}') { return null; }
    try {
      const doc = JSON.parse(json);
      return doc && typeof doc === 'object' ? doc : null;
    } catch {
      return null;
    }
  }

  imageUrl(artifactId: string, fileName: string = DAYDREAM_IMAGE_FILE, width?: number): string {
    const url = `${this.configService.config.situViewWebApiBaseUrl}/home/GetImage?artifactId=${artifactId}&fileName=${encodeURIComponent(fileName)}`;
    return width ? `${url}&width=${width}` : url;
  }

  /**
   * Generate a Daydream from a parent facade. SituView pulls the source image, calls the model
   * and stores the result. Synchronous on the server, including the model call, so the caller
   * shows a busy state rather than polling.
   */
  generate(args: DaydreamGenerateArgs): Promise<GetSituFacadeResponse> {
    const request = new CreateDaydreamFacadeRequest();
    request.companyId = args.companyId;
    request.renderedOutputId = args.renderedOutputId;
    request.sourceFileName = args.sourceFileName;
    request.promptText = args.promptText;
    request.promptJson = args.promptJson;
    return this.situViewClient.createDaydreamFacade(request);
  }

  situLinkUrl(renderedOutputId: string): string {
    const config = this.configService.config;
    return config.situLinkBaseUrl
      ? `https://facade.${config.situLinkBaseUrl}/${renderedOutputId}`
      : `${config.situPortalBaseUrl}/${renderedOutputId}`;
  }

  private remember(details: DaydreamDetails): void {
    if (details.id && !isPending(details.status)) { this.details.set(details.id, details); }
  }
}
